import {
  Title,
  Alert,
  Group,
  Button,
  Image,
  Text,
  Loader,
  Center,
  Card,
  Box,
} from "@mantine/core";
import logo from "../../assets/ramboll-logo.svg";
import bgImage from "../../assets/rims_bg_launch_screen.png";
import { useEffect, useState } from "react";
import { IconInfoCircle } from "@tabler/icons-react";
import axios from "axios";
import { clientId } from "../../data/msalConfig";
import { useMsal, useIsAuthenticated } from "@azure/msal-react";
import useTransientStore from "../../data/transcientStore";
import {
  getUserById,
  getUserDetails,
  getUserIdByEmail,
} from "../../services/UserService";
import { InteractionStatus } from "@azure/msal-browser";
import { Navigate } from "react-router";
import { NotRegistered } from "../notRegistered/NotRegistered";
import AccessDenied from "../../components/auth/AccessDenied";
// Translation API requires auth - disabled for pre-auth login page
// import { useTranslation } from "../../hooks/useTranslation";
import { AUTH_ENABLED } from "../../data/authConfig";

const backgroundImageUrl = bgImage;

export default function Login() {
  // Translation API requires auth - disabled for pre-auth login page
  // const selectedAddressSystem = useTransientStore((state) => state.selectedAddressSystem);
  // const addrIdFromStore = selectedAddressSystem?.id || 0;
  // const { getTranslation } = useTranslation({ pageId: 320, addressSystemId: addrIdFromStore || 0, autoLoad: true });

  const { inProgress, accounts, instance } = useMsal();
  const isAuthenticated = useIsAuthenticated();

  const [isTokenValid, setIsTokenValid] = useState(false);
  const [error, setError] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isRegistrationLoading, setIsRegistrationLoading] = useState(true);
  const [accessDeniedInfo, setAccessDeniedInfo] = useState<{
    description: string | null;
    correlationId: string | null;
  } | null>(null);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [postLogoutLoading, setPostLogoutLoading] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => {
      setPostLogoutLoading(false);
    }, 1000);
    return () => clearTimeout(timer);
  }, []);

  const setToken = useTransientStore((state) => state.setToken);
  const isRegistered = useTransientStore((state) => state.isUserRegistered);
  const setIsUserRegistered = useTransientStore(
    (state) => state.setIsUserRegistered,
  );
  const setCurrentUser = useTransientStore((state) => state.setCurrentUser);
  const setCurrentUserId = useTransientStore((state) => state.setCurrentUserId);
  const setCurrentUserEmail = useTransientStore(
    (state) => state.setCurrentUserEmail,
  );
  const setCurrentUsername = useTransientStore(
    (state) => state.setCurrentUsername,
  );
  const setCurrentUserPhotoUrl = useTransientStore(
    (state) => state.setCurrentUserPhotoUrl,
  );
  const setCurrentUserRole = useTransientStore(
    (state) => state.setCurrentUserRole,
  );
  const setCurrentUserAddressSystems = useTransientStore(
    (state) => state.setCurrentUserAddressSystems,
  );

  const backendBaseUrl = process.env.REACT_APP_API_URL;

  const handleLogin = async () => {
    setIsRedirecting(true);
    try {
      await instance.loginRedirect({
        scopes: [`api://${clientId}/access_as_user`],
      });
    } catch (err) {
      setIsRedirecting(false);
      console.error("Login failed", err);
      setError(true);
    }
  };

  const acquireAccessToken = async (account: any) => {
    try {
      const result = await instance.acquireTokenSilent({
        account,
        scopes: [`api://${clientId}/access_as_user`],
      });
      if (result.accessToken) {
        setToken(result.accessToken);
        return result.accessToken;
      }
    } catch (error) {
      console.error(
        "Silent token acquisition failed, please try logging in again.",
        error,
      );
      setIsTokenValid(false);
      setToken(null);
    }
    return null;
  };

  // Fetch user photo from Microsoft Graph API
  const fetchUserPhoto = async () => {
    try {
      const account = accounts[0];
      if (!account) return;

      // Acquire token for Microsoft Graph API
      const graphResult = await instance.acquireTokenSilent({
        account,
        scopes: ["User.Read"],
      });

      if (!graphResult.accessToken) return;

      const response = await fetch(
        "https://graph.microsoft.com/v1.0/me/photo/$value",
        {
          headers: {
            Authorization: `Bearer ${graphResult.accessToken}`,
          },
        },
      );

      if (!response.ok) {
        console.log("No profile photo available");
        return;
      }

      const blob = await response.blob();
      // Convert to a base64 data URL so the photo can be persisted in the
      // store and survive full page reloads (e.g. switching address system).
      // Object URLs created via URL.createObjectURL are invalidated on reload.
      const photoUrl = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result as string);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
      setCurrentUserPhotoUrl(photoUrl);
    } catch (error) {
      console.error("Failed to fetch user photo:", error);
      setCurrentUserPhotoUrl(null);
    }
  };

  // Helper: Set user info from claims
  const setUserInfoFromClaims = (account: any) => {
    const claims = account.idTokenClaims;
    const email = (
      (claims?.email as string) || (claims?.preferred_username as string)
    )?.toLowerCase();
    const name = (claims?.name as string) || email || "";
    setCurrentUser(name);
    setCurrentUserEmail(email || "");
    return { email, name };
  };

  // Helper: Setup address systems
  // Returns true if user is valid, false if user ID is -1 (not found in DB)
  const setupAddressSystems = async (
    email: string,
    accessToken: string,
  ): Promise<boolean> => {
    try {
      await axios.post(
        `${backendBaseUrl}/api/UserManagement/checkUser`,
        { email },
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        },
      );
      const userId = await getUserIdByEmail(email);

      // If userId is -1, user is not in the database - mark as not registered
      if (userId === -1) {
        setCurrentUserId(0);
        setCurrentUserAddressSystems([]);
        return false;
      }

      setCurrentUserId(userId);
      const user = await getUserById(userId);

      // Update currentUser with name from database (overrides Azure AD name)
      if (user.name) {
        setCurrentUser(user.name);
      } else if (user.username) {
        setCurrentUser(user.username);
      }

      // Store username for reference
      if (user.username) {
        setCurrentUsername(user.username);
      }

      // Set user's role from database
      if (user.role && user.role.id) {
        setCurrentUserRole(user.role.id);
      }

      if (user.addresses && Array.isArray(user.addresses)) {
        setCurrentUserAddressSystems(user.addresses.map((a: any) => ({ ...a, name: a.name?.trim() ?? '' })));
      } else {
        setCurrentUserAddressSystems([]);
      }
      return true;
    } catch (err) {
      console.error("User lookup failed", err);
      return false;
    }
  };

  // Helper: Check registration
  const checkRegistration = async (username: string) => {
    try {
      const userDetails = await getUserDetails(username);
      setIsUserRegistered(!!userDetails);
    } catch (e) {
      console.error("Registration lookup failed", e);
      setIsUserRegistered(false);
    }
  };

  useEffect(() => {
    // Parse potential AAD error in URL (from failed assignment / group membership)
    const hash = window.location.hash || "";
    const search = window.location.search || "";
    const combined = `${hash} ${decodeURIComponent(search)}`;
    // Common code: AADSTS50105 - user not assigned to application
    if (
      /AADSTS50105/i.test(combined) ||
      /not a direct member of a group/i.test(combined)
    ) {
      const descMatch = combined.match(/error_description=([^&]+)/i);
      const rawDesc = descMatch
        ? decodeURIComponent(descMatch[1].replace(/\+/g, " "))
        : null;
      const corrMatch = combined.match(/correlation_id=([^&]+)/i);
      const corrId = corrMatch ? corrMatch[1] : null;
      setAccessDeniedInfo({ description: rawDesc, correlationId: corrId });
    }

    const checkTokenAndUser = async () => {
      if (accounts.length > 0) {
        setIsRegistrationLoading(true);
        if (isRegistered === null) {
          setIsUserRegistered(null); // explicit unknown while we refetch
        }
        const account = accounts[0];
        const expiryDate = account.idTokenClaims?.exp
          ? account.idTokenClaims.exp * 1000
          : 0;

        // Always acquire token first, then validate
        let accessToken = await acquireAccessToken(account);
        setIsTokenValid(!!accessToken);

        const { email } = setUserInfoFromClaims(account);
        if (email && accessToken) {
          const userIsValid = await setupAddressSystems(email, accessToken);
          // If userId was -1 (user not in DB), mark as not registered immediately
          if (!userIsValid) {
            setIsUserRegistered(false);
            setIsRegistrationLoading(false);
            setIsLoading(false);
            return;
          }
        }
        // Fetch user profile photo
        await fetchUserPhoto();
        await checkRegistration(accounts[0].username);
        setIsRegistrationLoading(false);
      } else {
        setIsRegistrationLoading(false);
      }
      setIsLoading(false);
    };
    checkTokenAndUser();
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accounts, instance]);

  // When auth is disabled, skip login and go straight to the app
  if (!AUTH_ENABLED) {
    return <Navigate to="/select-address-system" />;
  }

  const icon = <IconInfoCircle />;

  if (
    isRedirecting ||
    isLoading ||
    inProgress !== InteractionStatus.None ||
    isRegistrationLoading ||
    postLogoutLoading ||
    (isAuthenticated && isRegistered === null)
  ) {
    return (
      <div
        style={{
          minHeight: "100vh",
          width: "100vw",
          backgroundImage: `url(${backgroundImageUrl})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Center style={{ width: "100vw", height: "100vh" }}>
          <Loader size={48} color="blue" type="bars" />
        </Center>
      </div>
    );
  }

  if (isAuthenticated && isTokenValid && isRegistered === true) {
    return <Navigate to="/select-address-system" />;
  }

  // Only show NotRegistered if loading is done and registration is definitely false
  if (isAuthenticated && isTokenValid && isRegistered === false) {
    return <NotRegistered />;
  }

  if (accessDeniedInfo) {
    return (
      <AccessDenied
        errorDescription={accessDeniedInfo.description}
        correlationId={accessDeniedInfo.correlationId}
      />
    );
  }

  return (
    <div
      style={{
        minHeight: "100vh",
        width: "100vw",
        backgroundImage: `url(${backgroundImageUrl})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "20px",
      }}
    >
      <Card
        shadow="xl"
        radius="lg"
        padding="xl"
        style={{
          width: "100%",
          maxWidth: 360,
          minHeight: 320,
          display: "flex",
          flexDirection: "column",
          backdropFilter: "blur(8px)",
          background: "rgba(255,255,255,0.85)",
          border: "1px solid rgba(255,255,255,0.6)",
        }}
      >
        <Box style={{ flex: 1, display: "flex", flexDirection: "column" }}>
          <Group justify="center" mb="md">
            <Title
              order={2}
              ta="center"
              style={{ fontWeight: 700, letterSpacing: 1 }}
            >
              RIMS
            </Title>
          </Group>
          {error && (
            <Alert
              variant="light"
              color="red"
              title="Login error" // getTranslation("TITLE_LOGIN_ERROR")
              icon={icon}
              mb="md"
            >
              Error logging in. Please try again.
            </Alert>
          )}
          <Button
            aria-label="Sign in" // getTranslation("ARIA_LOGINSUBMITBUTTON")
            type="submit"
            variant="filled"
            color="blue"
            fullWidth
            size="md"
            onClick={handleLogin}
            mt="sm"
          >
            Login With Microsoft
          </Button>
        </Box>
        <Group
          justify="center"
          mt="lg"
          gap={4}
          style={{ flexDirection: "row" }}
        >
          <Image src={logo} alt="Ramboll logo" maw={108} />
          <Text size="sm" c="dimmed" mt={4}>
            &copy; RIMS {new Date().getFullYear()}
          </Text>
        </Group>
      </Card>
    </div>
  );
}
